"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFundingRequestGeneratorWithLoanMechanics = void 0;
const _ = __importStar(require("lodash"));
const common_1 = require("@finley-technologies/common");
const accrual_1 = require("./accrual");
const dates_1 = require("./dates");
const calculateTotal = (amounts) => common_1.utils.jsRound({
    value: amounts.reduce((sum, amount) => sum + amount, 0),
    precision: 2,
});
const getLoanPricing = ({ pricingOption, commitment, benchmarkRates, targetFundingDate, initialDeterminationDate, }) => {
    const lookbackDays = pricingOption.type === 'benchmark'
        ? pricingOption.determination.lookback.days
        : 0;
    const determinationDate = initialDeterminationDate ??
        common_1.date.addBusinessDays(targetFundingDate, -1 * lookbackDays);
    const benchmarkName = pricingOption.type === 'benchmark' ? pricingOption.benchmark : 'Fixed';
    const publishedRates = pricingOption.type === 'benchmark'
        ? benchmarkRates[pricingOption.benchmark] ?? []
        : [{ effectiveDate: determinationDate, rate: pricingOption.value }];
    const resolvedRate = publishedRates.find((rate, idx) => {
        // Automatic selection if date matches exactly
        if (rate.effectiveDate === determinationDate) {
            return true;
        }
        // Otherwise, there may be a mismatch in holiday schedules or lack of published rate availability
        // Proceed by checking against next published date to provide the latest available rate prior to determination date as fallback
        const nextRate = publishedRates[idx + 1];
        if (nextRate) {
            // If next rate is past determination date, return
            if (nextRate.effectiveDate > determinationDate) {
                return true;
            }
            return false;
        }
        // This is the last rate, automatically select for use
        return true;
    })?.rate ?? 0;
    const benchmarkRate = resolvedRate;
    const margin = pricingOption?.margin.value ?? 0;
    const allInRate = benchmarkRate + margin;
    let serviceFee = 0;
    for (const loan of commitment.loans) {
        for (const lender of loan.lenders) {
            if (lender.servicerFee) {
                serviceFee = lender.servicerFee.rate;
            }
        }
    }
    return {
        benchmark: {
            name: benchmarkName,
            determinationDate,
            rate: benchmarkRate,
        },
        waApplicableMarginRate: margin,
        allInRate,
        serviceFee,
    };
};
const getAllocatedShares = ({ commitmentKey, commitmentAmount, requestAmount, lenders, targetFundingDate, maturityDate, pricing, borrowerBankAccount, lenderBankAccounts, fundingRequestComponents, readOnly, }) => {
    let remainingAmount = requestAmount;
    const allocatedShares = lenders.map((lender) => {
        // Match existing component first, then assign amount depending on mode (read-only)
        const bankAccount = lenderBankAccounts.find((account) => account.organizationId === lender.entity.id);
        // May need to monitor and revisit if we have multiple requests and
        // components associated with the same "lender" for a loan
        const fundingRequestComponent = fundingRequestComponents.find((item) => item.type === 'drawRequest' &&
            item.sourceBankAccountId === bankAccount?.id &&
            (!item.commitmentKey || item.commitmentKey === commitmentKey));
        (0, common_1.finleyAssert)(fundingRequestComponent, `Expected to find funding component when updating draw request amount`);
        const proRataAmount = common_1.utils.jsRound({
            value: common_1.utils.divideByOrZero(lender.amount, commitmentAmount) *
                requestAmount,
            precision: 2,
        });
        const lenderAllocation = Math.min(proRataAmount, remainingAmount);
        remainingAmount = common_1.utils.jsRound({
            value: remainingAmount - lenderAllocation,
            precision: 2,
        });
        const shareAmount = readOnly
            ? fundingRequestComponent.amount
            : lenderAllocation;
        return {
            fundingRequestComponentId: fundingRequestComponent.id,
            lender: { organizationId: lender.entity.id, name: lender.entity.name },
            bankAccounts: {
                source: bankAccount?.name ?? '',
                destination: borrowerBankAccount?.name ?? '',
            },
            percent: common_1.utils.divideByOrZero(shareAmount, requestAmount),
            amount: shareAmount,
            applicableMargin: pricing.applicableMargin,
            projectedInterest: (0, accrual_1.calculateProjectedInterest)({
                balance: shareAmount,
                dayCountConvention: 'Actual / 360',
                targetFundingDate,
                maturityDate,
                allInRate: pricing.allInRate,
            }),
        };
    });
    // TODO: more advance checks, this is sufficient for now
    // Defaulting to sending all remaining to the 1st lender share in the list (implicit expectation in configuration that this would be the lead lender and/or agent)
    // The remaining amount should always come out to less than the rounding tolerance for the group (1 penny * number of shares)
    if (remainingAmount > 0 && allocatedShares[0]) {
        allocatedShares[0].amount = common_1.utils.jsRound({
            value: allocatedShares[0].amount + remainingAmount,
            precision: 2,
        });
    }
    return allocatedShares;
};
const getDrawRequest = ({ drawRequest, pricingOption, loanMechanics, benchmarkRates, borrowerBankAccount, lenderBankAccounts, fundingRequestComponents, updateParams, }) => {
    (0, common_1.finleyAssert)(drawRequest.loan, 'Loan attributes must be present on draw request');
    const commitment = loanMechanics.commitments.find((item) => item.key === drawRequest.key);
    (0, common_1.finleyAssert)(commitment, 'Commitment must be present');
    const loanConfiguration = loanMechanics.loans.find((item) => item.pricingOptionKey === pricingOption.key);
    (0, common_1.finleyAssert)(loanConfiguration, 'Global loan configuration must be present');
    const loan = commitment.loans.find((item) => item.loanKey === loanConfiguration.key);
    const requestAmount = loan ? updateParams.amount : 0;
    // Recalculate pricing if (1) pricing option has changed OR (2) target funding date has changed
    // Amount and maturity date changes only affect the projected interest for each share
    const pricing = drawRequest.pricingOption.key !== pricingOption.key ||
        drawRequest.targetFundingDate !== updateParams.targetFundingDate ||
        (updateParams.determinationDate &&
            drawRequest.loan.pricing.benchmark.determinationDate !==
                updateParams.determinationDate)
        ? getLoanPricing({
            pricingOption,
            commitment,
            benchmarkRates,
            targetFundingDate: updateParams.targetFundingDate,
            initialDeterminationDate: updateParams.determinationDate,
        })
        : drawRequest.loan.pricing;
    const shares = getAllocatedShares({
        commitmentKey: commitment.key,
        commitmentAmount: commitment.limit.amount,
        requestAmount,
        lenders: loan ? loan.lenders : [],
        targetFundingDate: updateParams.targetFundingDate,
        maturityDate: updateParams.maturityDate,
        pricing: {
            applicableMargin: pricing.waApplicableMarginRate,
            allInRate: pricing.allInRate,
        },
        borrowerBankAccount,
        lenderBankAccounts,
        fundingRequestComponents,
        readOnly: false,
    });
    return {
        ...drawRequest,
        requestAmount,
        targetFundingDate: updateParams.targetFundingDate,
        pricingOption: { key: pricingOption.key, name: pricingOption.name },
        hide: !loan,
        loan: {
            ...drawRequest.loan,
            externalLoanId: updateParams.externalLoanId ?? undefined,
            maturityDate: updateParams.maturityDate,
            pricing,
            shares,
        },
    };
};
const validateSubmission = ({ loanRequests, allowMultipleRequests, globalUnused, upsizeRequest, }) => {
    // *** Validate Draw ***
    if (upsizeRequest && loanRequests.length !== 1) {
        return {
            isValid: false,
            errors: [
                'Invalid draw request amount - only one draw request is allowed for upsize requests',
            ],
        };
    }
    if (!allowMultipleRequests && loanRequests.length !== 1) {
        return {
            isValid: false,
            errors: ['Invalid submission - only one draw request is allowed.'],
        };
    }
    if (allowMultipleRequests && loanRequests.length < 1) {
        return {
            isValid: false,
            errors: [
                'Invalid submission - at least one draw request must be present.',
            ],
        };
    }
    if (!loanRequests.some(({ requestAmount }) => requestAmount >= 0)) {
        return {
            isValid: false,
            errors: [
                'Invalid submission - at least one non-zero draw request must be present.',
            ],
        };
    }
    // Global
    const totalDraw = common_1.utils.jsRound({
        value: _.sumBy(loanRequests, (opt) => opt.requestAmount),
        precision: 2,
    });
    if (totalDraw > globalUnused) {
        return {
            isValid: false,
            errors: [
                `Invalid draw request amount - maximum draw cannot exceed global unused commitment: $${globalUnused.toLocaleString()}`,
            ],
        };
    }
    const globalMinimum = loanRequests[0]?.constraints.minimum ?? 0;
    if (totalDraw > 0 && totalDraw < globalMinimum) {
        return {
            isValid: false,
            errors: [
                `Invalid draw request amount - required to be either $0 or an amount greater than the minimum draw: $${globalMinimum.toLocaleString()}`,
            ],
        };
    }
    // Individual
    const individualDrawTests = loanRequests.every((drawRequest) => {
        if (drawRequest.requestAmount < 0) {
            return false;
        }
        if (drawRequest.requestAmount > 0 &&
            drawRequest.requestAmount < drawRequest.constraints.minimum) {
            return false;
        }
        if (drawRequest.requestAmount > 0 &&
            drawRequest.requestAmount > drawRequest.constraints.maximum) {
            return false;
        }
        // 0 here, true
        return true;
    });
    if (!individualDrawTests) {
        return {
            isValid: false,
            errors: [
                'Invalid draw request amounts. Please ensure that no negative values are present and also that maximum constraints are respected.',
            ],
        };
    }
    return { isValid: true };
};
const getFundingRequestGeneratorWithLoanMechanics = (params) => {
    (0, common_1.finleyAssert)(params.fundingMechanicsV2Serialized, `Funding mechanics must be provided for request`);
    const { fundingMechanicsV2Serialized, loanBalances, mode, allowedActions, loans, fundingRequestToDuplicate, } = params;
    const submittedLoans = loans ?? [];
    if (mode === 'upsize') {
        (0, common_1.finleyAssert)(submittedLoans.length === 1, `Upsizing request requires 1 loan, but found ${submittedLoans.length}`);
    }
    // Locally scoped components to avoid mutation at parent scope
    const currentFundingRequestComponents = _.cloneDeep(params.currentFundingRequestComponents);
    const fundingMechanics = JSON.parse(fundingMechanicsV2Serialized);
    const { loanMechanics } = fundingMechanics;
    // TODO: @sachinr4 remove these hacky things
    const isTermLoan = Boolean(loanMechanics?.isTermLoan);
    const totalOutstandingBalance = loanBalances.reduce((sum, { amount }) => sum + amount, 0);
    const commitmentBalances = new Map();
    for (const loanBalance of loanBalances) {
        const entry = commitmentBalances.get(loanBalance.commitmentKey) ?? 0;
        commitmentBalances.set(loanBalance.commitmentKey, entry + loanBalance.amount);
    }
    const globalCommitment = isTermLoan
        ? totalOutstandingBalance
        : loanMechanics.commitments.reduce((sum, { limit }) => (limit.sublimit ? sum : sum + limit.amount), 0);
    const globalUnused = Math.max(globalCommitment - totalOutstandingBalance, 0);
    const fundingRequestId = currentFundingRequestComponents[0].fundingRequestId;
    const borrowerBankAccount = fundingMechanics.bankAccounts.find((account) => account.organizationId === fundingMechanics.borrower.organizationId);
    (0, common_1.finleyAssert)(borrowerBankAccount, 'Borrower bank account must be present');
    (0, common_1.finleyAssert)(loanMechanics.pricingOptions.length > 0, 'Pricing options must be provided for funding request');
    const existingMaturityDate = ((fundingRequestToDuplicate?.resolvedLoans ??
        [])[0]?.dates.maturityDate ??
        submittedLoans[0]?.dates.maturityDate ??
        null); // Need to cast since TS doesn't infer this correctly
    const drawRequestComponent = currentFundingRequestComponents.find((component) => component.type === 'drawRequest' && component.amount > 0);
    const initialTargetFundingDate = (0, dates_1.generateInitialTargetFundingDate)({
        postingDate: drawRequestComponent?.postingDate ?? null,
        targetFundingDate: drawRequestComponent?.targetFundingDate ?? null,
        maturityDate: existingMaturityDate,
    });
    const fundingRequestToDuplicatePricingOption = loanMechanics.pricingOptions.find((option) => option.key ===
        (fundingRequestToDuplicate?.resolvedLoans ?? [])[0]?.pricing.key);
    const fundingRequestToDuplicateInvoiceMechanicsKey = (fundingRequestToDuplicate?.resolvedLoans ?? [])[0]?.invoiceMechanics
        ?.key ?? null;
    const submittedLoanPricingOption = submittedLoans[0]
        ? loanMechanics.pricingOptions.find((option) => option.key === submittedLoans[0].pricing.key)
        : null;
    const submittedLoanInvoiceMechanicsKey = submittedLoans[0]
        ? submittedLoans[0].invoiceMechanics?.key
        : null;
    const initialPricingOption = fundingRequestToDuplicatePricingOption ??
        submittedLoanPricingOption ??
        loanMechanics.pricingOptions[0];
    const initialInvoiceMechanicsKey = fundingRequestToDuplicateInvoiceMechanicsKey ??
        submittedLoanInvoiceMechanicsKey ??
        undefined;
    const global = {
        externalLoanId: (fundingRequestToDuplicate?.resolvedLoans ?? [])[0]?.externalLoanId ??
            submittedLoans[0]?.externalLoanId ??
            null,
        targetFundingDate: initialTargetFundingDate,
        maturityDate: existingMaturityDate ?? common_1.date.addMonths(initialTargetFundingDate, 1),
        pricingOption: initialPricingOption,
        invoiceMechanicsKey: initialInvoiceMechanicsKey,
    };
    // Match cases for initial setup
    const drawComponents = currentFundingRequestComponents.filter((component) => component.type === 'drawRequest');
    // Set up initial set of requests based on commitment and balance
    // After setup, then perform updates based on allowed actions and mode (set pricing/amounts if read only)
    const drawRequests = new Map();
    for (const commitment of loanMechanics.commitments) {
        if (mode === 'upsize') {
            const loan = submittedLoans[0];
            if (loan && loan.commitmentKey !== commitment.key)
                continue;
            // Reset global pricing option in this mode to one that exists
            const pricingOption = loanMechanics.pricingOptions.find(({ key }) => key === loan.pricing.key);
            (0, common_1.finleyAssert)(pricingOption, 'Pricing option for upsizing is invalid');
            global.pricingOption = pricingOption;
        }
        // Maximum constraint: unused of commitment
        // Sublimit: minimum of global unused and sublimit
        // Example: global $100mm, C1 $50mm, C2 $50mm, C3 sublimit $30mm
        //          -> draw up to $30mm on C3, as long as global unused is higher
        const commitmentUnused = commitment.limit.amount - (commitmentBalances.get(commitment.key) ?? 0);
        const maximum = Math.min(commitmentUnused, globalUnused);
        const loanConfiguration = loanMechanics.loans.find((item) => item.pricingOptionKey === global.pricingOption.key);
        (0, common_1.finleyAssert)(loanConfiguration, 'Global loan configuration must be present');
        const targetLoan = commitment.loans.find((item) => item.loanKey === loanConfiguration.key);
        // If the target loan isn't defined, fall back to the first defined loan
        // This means the pricing option isn't available for this commitment
        // Commitment will be set to `hide: true` when this occurs
        const firstLoanOption = commitment.loans[0];
        // Find a matching loan, if one was submitted
        const matchingSubmittedLoan = submittedLoans.find((loan) => loan.commitmentKey === commitment.key);
        const loanPricing = getLoanPricing({
            commitment,
            pricingOption: global.pricingOption,
            benchmarkRates: fundingMechanics.benchmarkRates,
            targetFundingDate: global.targetFundingDate,
            initialDeterminationDate: matchingSubmittedLoan?.pricing.current.baseRate.determinationDate ??
                null,
        });
        // This logic must stay in place to preserve historical request accuracy
        let loanRequestComponents;
        // TODO: fundingRequestToDuplicate stuff seems broken for multi-commitment structures
        if (fundingRequestToDuplicate) {
            // Find a matching loan, if one was submitted
            const matchingLoanToDuplicate = (fundingRequestToDuplicate.resolvedLoans ?? []).find((loan) => loan.commitmentKey === commitment.key);
            loanRequestComponents = drawComponents.map((drawComponent) => {
                const matchingComponent = fundingRequestToDuplicate.fundingRequestComponents.find((component) => component.sourceBankAccountId === drawComponent.sourceBankAccountId);
                return {
                    ...drawComponent,
                    amount: matchingLoanToDuplicate && matchingComponent
                        ? matchingComponent.amount
                        : drawComponent.amount,
                };
            });
        }
        else {
            loanRequestComponents = drawComponents
                // Filter on components using commitment key, if set
                // Otherwise, all components are set on all requests
                // When no commitment key is available, only one loan will be submitted
                .filter((component) => !component.commitmentKey ||
                component.commitmentKey === commitment.key)
                // If a matching loan was found, set original component amount, otherwise 0
                // This is primarily to support requests for which commitment key was not set
                .map((component) => ({
                ...component,
                amount: matchingSubmittedLoan ? component.amount : 0,
            }));
        }
        const requestAmount = calculateTotal(loanRequestComponents.map((item) => item.amount));
        const drawRequest = {
            key: commitment.key,
            name: commitment.name,
            fxDetails: null,
            initialAmount: 0, // Unused
            requestAmount,
            targetFundingDate: global.targetFundingDate,
            constraints: {
                maximum,
                minimum: 0,
                increment: null,
            },
            attachmentLimit: 1, // Not used here, set to 1 by default
            pricingOption: {
                key: global.pricingOption.key,
                name: global.pricingOption.name,
            },
            hide: !targetLoan,
            loan: {
                key: targetLoan ? targetLoan.loanKey : firstLoanOption.loanKey,
                externalLoanId: global.externalLoanId ?? undefined,
                maturityDate: global.maturityDate,
                pricing: loanPricing,
                invoicingMechanicsKey: global.invoiceMechanicsKey,
                shares: getAllocatedShares({
                    commitmentKey: commitment.key,
                    commitmentAmount: commitment.limit.amount,
                    requestAmount,
                    lenders: targetLoan ? targetLoan.lenders : firstLoanOption.lenders,
                    targetFundingDate: global.targetFundingDate,
                    maturityDate: global.maturityDate,
                    pricing: {
                        applicableMargin: loanPricing.waApplicableMarginRate,
                        allInRate: loanPricing.allInRate,
                    },
                    borrowerBankAccount,
                    lenderBankAccounts: fundingMechanics.bankAccounts,
                    fundingRequestComponents: loanRequestComponents,
                    readOnly: allowedActions === 'readOnly',
                }),
            },
        };
        drawRequests.set(commitment.key, drawRequest);
    }
    const expectedRequests = mode === 'upsize'
        ? [{ commitmentKey: submittedLoans[0].commitmentKey }]
        : loanMechanics.commitments.map((commitment) => ({
            commitmentKey: commitment.key,
        }));
    return {
        deficiency: 0,
        summary: {
            commitment: { amount: globalCommitment, currency: 'USD' },
            // can't use totalBorrowingBaseAmount because it's no longer accurate
            // db records for totalBorrowingBaseAmount need to be migrated before use @danitian
            borrowingBaseAmount: 0,
            maximumAvailableAmount: globalUnused,
            outstandingBalance: totalOutstandingBalance,
            facilityUtilization: common_1.utils.divideByOrZero(totalOutstandingBalance, globalCommitment),
            pendingRequests: params.pendingRequests.map((req) => ({
                ...req,
                fundingRequestComponentId: req.id,
                fundingRequestId,
            })),
            drawAvailability: globalUnused,
            recycleAvailability: 0,
            borrowingBaseReportMetadata: null,
            assignedEligibleExposureSinceLastApprovedFundingRequest: null,
            borrowingBaseReportMetadataForLastApprovedFundingRequest: null,
        },
        data: {
            resolvedLoan: {
                get: () => submittedLoans[0] ?? undefined,
            },
            loanType: {
                get: () => 'origination',
                set: () => {
                    return [];
                },
            },
            pricingOptions: {
                get: () => {
                    return loanMechanics.pricingOptions.map((option) => ({
                        key: option.key,
                        name: option.name,
                        lookbackDays: option.type === 'benchmark'
                            ? option.determination.lookback.days
                            : 0,
                        margin: option.margin.value,
                        invoiceMechanics: option.invoiceMechanics,
                    }));
                },
                set: (request) => {
                    const newPricingOption = loanMechanics.pricingOptions.find((option) => option.key === request.pricingOptionKey);
                    (0, common_1.finleyAssert)(newPricingOption, 'Invalid pricing option');
                    global.pricingOption = newPricingOption;
                    global.invoiceMechanicsKey = request.invoiceMechanicsKey;
                    // Reset draws
                    for (const drawRequest of drawRequests.values()) {
                        (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                        const updatedDrawRequest = getDrawRequest({
                            drawRequest,
                            pricingOption: global.pricingOption,
                            loanMechanics,
                            benchmarkRates: fundingMechanics.benchmarkRates,
                            borrowerBankAccount,
                            lenderBankAccounts: fundingMechanics.bankAccounts,
                            fundingRequestComponents: currentFundingRequestComponents,
                            updateParams: {
                                amount: drawRequest.requestAmount,
                                externalLoanId: drawRequest.loan.externalLoanId ?? null,
                                maturityDate: drawRequest.loan.maturityDate,
                                targetFundingDate: drawRequest.targetFundingDate,
                                determinationDate: null, // Automatically reset
                            },
                        });
                        drawRequests.set(drawRequest.key, updatedDrawRequest);
                    }
                    return ['pricingOptions', 'drawRequests', 'maturityDate'];
                },
            },
            maturityDate: {
                get: () => {
                    return global.maturityDate;
                },
                set: (request) => {
                    global.maturityDate = request.date;
                    for (const drawRequest of drawRequests.values()) {
                        (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                        const updatedDrawRequest = getDrawRequest({
                            drawRequest,
                            pricingOption: global.pricingOption,
                            loanMechanics,
                            benchmarkRates: fundingMechanics.benchmarkRates,
                            borrowerBankAccount,
                            lenderBankAccounts: fundingMechanics.bankAccounts,
                            fundingRequestComponents: currentFundingRequestComponents,
                            updateParams: {
                                amount: drawRequest.requestAmount,
                                externalLoanId: drawRequest.loan.externalLoanId ?? null,
                                maturityDate: global.maturityDate,
                                targetFundingDate: drawRequest.targetFundingDate,
                                determinationDate: null, // Automatically reset
                            },
                        });
                        drawRequests.set(drawRequest.key, updatedDrawRequest);
                    }
                    return ['maturityDate'];
                },
            },
            externalLoanId: {
                get: () => {
                    return global.externalLoanId ?? undefined;
                },
                set: (request) => {
                    global.externalLoanId = request.externalLoanId || null; // Set empty string to null
                    for (const drawRequest of drawRequests.values()) {
                        (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                        const updatedDrawRequest = getDrawRequest({
                            drawRequest,
                            pricingOption: global.pricingOption,
                            loanMechanics,
                            benchmarkRates: fundingMechanics.benchmarkRates,
                            borrowerBankAccount,
                            lenderBankAccounts: fundingMechanics.bankAccounts,
                            fundingRequestComponents: currentFundingRequestComponents,
                            updateParams: {
                                amount: drawRequest.requestAmount,
                                externalLoanId: global.externalLoanId,
                                maturityDate: drawRequest.loan.maturityDate,
                                targetFundingDate: drawRequest.targetFundingDate,
                                determinationDate: null, // Automatically reset
                            },
                        });
                        drawRequests.set(drawRequest.key, updatedDrawRequest);
                    }
                    return ['externalLoanId'];
                },
            },
            recycleRequests: {
                isGlobalFundingDate: true,
                get: () => {
                    return [];
                },
                set: () => {
                    return [];
                },
            },
            netRecycleRequest: {
                get: () => {
                    return null;
                },
                set: () => {
                    return [];
                },
            },
            drawRequests: {
                isGlobalFundingDate: true,
                get: () => {
                    const selections = [];
                    expectedRequests.forEach(({ commitmentKey }) => {
                        const drawRequest = drawRequests.get(commitmentKey);
                        if (drawRequest && !drawRequest?.hide) {
                            selections.push(drawRequest);
                        }
                    });
                    return selections;
                },
                set: (request) => {
                    if (request.key) {
                        for (const drawRequest of drawRequests.values()) {
                            (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                            if (request.key === drawRequest.key) {
                                const updatedDrawRequest = getDrawRequest({
                                    drawRequest,
                                    pricingOption: global.pricingOption,
                                    loanMechanics,
                                    benchmarkRates: fundingMechanics.benchmarkRates,
                                    borrowerBankAccount,
                                    lenderBankAccounts: fundingMechanics.bankAccounts,
                                    fundingRequestComponents: currentFundingRequestComponents,
                                    updateParams: {
                                        amount: request.amount,
                                        externalLoanId: 
                                        // External loan ID can be cleared and set to null
                                        // Check for explicit presence of a string or null
                                        typeof request.externalLoanId === 'string' ||
                                            request.externalLoanId === null
                                            ? request.externalLoanId
                                            : drawRequest.loan.externalLoanId ?? null,
                                        maturityDate: request.maturityDate ?? drawRequest.loan.maturityDate,
                                        targetFundingDate: request.targetFundingDate ??
                                            drawRequest.targetFundingDate,
                                        determinationDate: request.determinationDate ?? null,
                                    },
                                });
                                drawRequests.set(drawRequest.key, updatedDrawRequest);
                            }
                        }
                    }
                    return [
                        'drawRequests',
                        'expectedSubmission',
                        'fundingImpact',
                        'recycleRequests',
                    ];
                },
            },
            targetFundingDate: {
                set: (request) => {
                    global.targetFundingDate = request.targetFundingDate;
                    for (const drawRequest of drawRequests.values()) {
                        (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                        const updatedDrawRequest = getDrawRequest({
                            drawRequest,
                            pricingOption: global.pricingOption,
                            loanMechanics,
                            benchmarkRates: fundingMechanics.benchmarkRates,
                            borrowerBankAccount,
                            lenderBankAccounts: fundingMechanics.bankAccounts,
                            fundingRequestComponents: currentFundingRequestComponents,
                            updateParams: {
                                amount: drawRequest.requestAmount,
                                externalLoanId: drawRequest.loan.externalLoanId ?? null,
                                maturityDate: drawRequest.loan.maturityDate,
                                targetFundingDate: global.targetFundingDate,
                                determinationDate: null, // Automatically reset
                            },
                        });
                        drawRequests.set(drawRequest.key, updatedDrawRequest);
                    }
                    return ['drawRequests', 'expectedSubmission', 'fundingImpact'];
                },
            },
            fundingImpact: {
                get: () => {
                    return {
                        commitment: { amount: globalCommitment, currency: 'USD' },
                        borrowingBaseAmount: null,
                        maximumAvailableAmount: null,
                        outstandingBalance: {
                            before: totalOutstandingBalance,
                            after: totalOutstandingBalance +
                                calculateTotal(Array.from(drawRequests.values()).map((val) => val.requestAmount)),
                        },
                        drawAvailability: {
                            before: globalCommitment - totalOutstandingBalance,
                            after: globalCommitment -
                                (totalOutstandingBalance +
                                    calculateTotal(Array.from(drawRequests.values()).map((val) => val.requestAmount))),
                        },
                        recycleAvailability: null,
                    };
                },
            },
            expectedSubmission: {
                get: () => {
                    return {
                        recycle: {
                            global: {
                                requestAmount: 0,
                                constraints: [],
                                targetFundingDate: global.targetFundingDate, // unused
                            },
                            requests: [], // unused
                        },
                        draw: {
                            global: {
                                requestAmount: Array.from(drawRequests.values()).reduce((sum, { requestAmount }) => sum + requestAmount, 0),
                                constraints: [],
                                pricingOptionKey: global.pricingOption.key,
                                invoiceMechanicsKey: global.invoiceMechanicsKey,
                                targetFundingDate: global.targetFundingDate,
                            },
                            requests: expectedRequests
                                .map(({ commitmentKey }) => {
                                const drawRequest = drawRequests.get(commitmentKey);
                                (0, common_1.finleyAssert)(drawRequest, 'Draw request must exist');
                                (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                                return {
                                    key: drawRequest.key,
                                    name: drawRequest.name,
                                    amount: drawRequest.requestAmount,
                                    loanKey: drawRequest.loan?.key,
                                    pricingOptionKey: drawRequest.pricingOption.key,
                                    invoiceMechanicsKey: global.invoiceMechanicsKey,
                                    targetFundingDate: drawRequest.targetFundingDate,
                                    shares: drawRequest.loan.shares,
                                    externalLoanId: drawRequest.loan.externalLoanId,
                                    maturityDate: drawRequest.loan.maturityDate,
                                    loanType: 'origination',
                                };
                            })
                                .filter((request) => request.amount > 0),
                        },
                    };
                },
            },
        },
        submission: {
            getPayload: () => {
                // To unblock zero value draw submissions, a form input needs to be added to indicate this
                // Otherwise, all draws are already set to 0 and will generate loans by default
                const loanRequests = Array.from(drawRequests.values()).filter(({ requestAmount }) => requestAmount > 0);
                const allowMultipleRequests = currentFundingRequestComponents
                    .filter((component) => component.type === 'drawRequest')
                    .every((component) => component.commitmentKey);
                const validationResult = validateSubmission({
                    loanRequests,
                    allowMultipleRequests,
                    globalUnused,
                    upsizeRequest: mode === 'upsize',
                });
                if (!validationResult.isValid) {
                    return {
                        isValid: false,
                        payload: null,
                        errors: validationResult.errors,
                    };
                }
                // Payload for this initial launch is always scoped to a single loan
                const submission = {
                    isValid: true,
                    payload: {
                        apiVersion: 2,
                        id: currentFundingRequestComponents[0].fundingRequestId,
                        fundingRequestComponents: currentFundingRequestComponents
                            .filter((component) => component.type !== 'drawRequest')
                            .map((component) => ({
                            id: component.id,
                            type: component.type,
                            amount: 0,
                            sourceBankAccountId: component.sourceBankAccountId,
                            destinationBankAccountId: component.destinationBankAccountId,
                            targetFundingDate: common_1.date.currentDateStr(), // Immediate settlement, don't care
                        })),
                        loanRequests: [],
                    },
                };
                for (const loanRequest of loanRequests) {
                    (0, common_1.finleyAssert)(loanRequest.loan, 'Request must contain loan details');
                    submission.payload.loanRequests.push({
                        externalLoanId: loanRequest.loan.externalLoanId,
                        maturityDate: loanRequest.loan.maturityDate,
                        loanType: 'origination',
                        pricingOptionKey: loanRequest.pricingOption.key,
                        loanKey: loanRequest.loan.key,
                        commitmentKey: loanRequest.key,
                        invoiceMechanicsKey: global.invoiceMechanicsKey,
                        initialDeterminationDate: loanRequest.loan.pricing.benchmark.determinationDate,
                    });
                    for (const share of loanRequest.loan.shares) {
                        const component = currentFundingRequestComponents.find(({ id }) => id === share.fundingRequestComponentId);
                        (0, common_1.finleyAssert)(component, 'Component must exist');
                        submission.payload.fundingRequestComponents.push({
                            id: component.id,
                            type: component.type,
                            amount: component.type === 'drawRequest' ? share.amount : 0,
                            sourceBankAccountId: component.sourceBankAccountId,
                            destinationBankAccountId: component.destinationBankAccountId,
                            targetFundingDate: loanRequest.targetFundingDate,
                        });
                    }
                }
                return submission;
            },
        },
    };
};
exports.getFundingRequestGeneratorWithLoanMechanics = getFundingRequestGeneratorWithLoanMechanics;
//# sourceMappingURL=requestLoanMechanics.js.map